'use client';

import type { ReactNode } from 'react';

import useInteractionTracker from '@blueshift-ui/telemetry/dist/hooks/use-interaction-tracker';
import { memo, useCallback } from 'react';

import type { CategorizedVideoData } from '../utils/video-loader';

import ConceptBreadcrumb from '../components/concept-breadcrumb';
import ConceptHeader from '../components/concept-header';
import ConceptSidebar from '../components/concept-sidebar';
import { Navigation } from '../components/navigation';
import SimpleFooter from '../components/simple-footer';

export interface ConceptMetadata {
  breadcrumbTitle: string;
  description: string;
  id: string;
  title: string;
}

export interface ConceptSidebarContent {
  icon?: string;
  notation?: {
    title: string;
    content: string[];
    note?: string;
  };
  notes?: {
    title: string;
    items: string[];
  };
  quickReference?: {
    title: string;
    items: Array<{
      label: string;
      value: string;
    }>;
  };
  remember?: {
    title: string;
    content: string;
  };
  theorem?: {
    title: string;
    content: string;
  };
  whyItWorks?: {
    title: string;
    content: string;
  };
}

interface MathConceptProps {
  children: ((videos: CategorizedVideoData | null) => ReactNode) | ReactNode;
  metadata: ConceptMetadata;
  sidebarContent: ConceptSidebarContent;
  videos: CategorizedVideoData | null;
}

const MathConcept = memo(
  ({ children, metadata, sidebarContent, videos }: MathConceptProps) => {
    const renderChildren =
      typeof children === 'function' ? children(videos) : children;

    const { trackEvent } = useInteractionTracker();

    const onContainerClick = useCallback(
      (e: React.MouseEvent) => {
        const target = e.target as HTMLElement | null;
        if (!target) return;
        const anchor = target.closest('a') as HTMLAnchorElement | null;
        if (!anchor) return;

        try {
          const url = new URL(anchor.href, window.location.origin);
          const path = url.pathname || '';
          if (path.includes('/hotmath_help/topics/')) {
            const parts = path.split('/').filter(Boolean);
            const last = parts[parts.length - 1] || '';
            const conceptId = last || '';
            if (conceptId) {
              void trackEvent({
                action: 'click-concept-link',
                category: 'page',
                label: `concept-${conceptId}`,
              });
            }
          }
        } catch {
          // ignore URL parsing errors
        }
      },
      [trackEvent]
    );

    return (
      <div className="surface-primary">
        <Navigation />
        <div className="background-flare"></div>

        <div className="container mx-auto max-w-7xl px-4 py-4 md:px-6 md:py-6" onClick={onContainerClick}>
          <ConceptBreadcrumb breadcrumbTitle={metadata.breadcrumbTitle} />

          <ConceptHeader
            description={metadata.description}
            title={metadata.title}
          />

          <div className="grid grid-cols-1 lg:grid-cols-5 lg:gap-6 xl:gap-10">
            <div className="order-2 lg:order-1 lg:col-span-2 mb-6 lg:mb-0">
              <ConceptSidebar
                breadcrumbTitle={metadata.breadcrumbTitle}
                sidebarContent={sidebarContent}
              />
            </div>

            <main
              className="order-1 lg:order-2 lg:col-span-3"
              id="main-content"
            >
              <div className="space-y-6 sm:space-y-8 lg:space-y-10">
                {renderChildren}
              </div>
            </main>
          </div>
        </div>

        <SimpleFooter />
      </div>
    );
  }
);

MathConcept.displayName = 'MathConcept';

export default MathConcept;
